import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Link } from "@/i18n/LocaleLink";
import { useLang } from "@/i18n/LanguageContext";

// Google Analytics 4 loads ONLY after the visitor accepts analytics cookies.
// The choice is stored in localStorage ("accepted" | "declined").
const GA_ID = "G-5853T8KVSK";
const CONSENT_KEY = "wd-analytics-consent";

const COPY = {
  en: {
    text: "This website uses Google Analytics cookies to understand how visitors experience it — only with your permission.",
    accept: "Accept",
    decline: "Decline",
    privacy: "Read the privacy policy",
  },
  nl: {
    text: "Deze website gebruikt cookies van Google Analytics om te begrijpen hoe bezoekers haar ervaren — alleen met jouw toestemming.",
    accept: "Accepteren",
    decline: "Weigeren",
    privacy: "Lees het privacybeleid",
  },
};

const getConsent = () => {
  const v = localStorage.getItem(CONSENT_KEY);
  return v === "accepted" || v === "declined" ? v : null;
};

let gaLoadPromise = null;

/* Lightweight GA4 event helper — fires only when analytics consent was given.
   If the tag hasn't attached yet (direct landing with prior consent), the event
   is queued in dataLayer and flushed once CookieConsent's loader runs. */
export const trackEvent = (name, params = {}) => {
  if (getConsent() !== "accepted") return;
  if (typeof window.gtag !== "function") {
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  window.gtag("event", name, params);
};

function loadGa() {
  if (gaLoadPromise) return gaLoadPromise;
  gaLoadPromise = new Promise((resolve) => {
    if (document.querySelector(`script[data-ga4="${GA_ID}"]`)) return resolve();
    window.dataLayer = window.dataLayer || [];
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("consent", "default", {
      analytics_storage: "denied",
      ad_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_ID}`;
    script.dataset.ga4 = GA_ID;
    script.onload = () => {
      window.gtag("consent", "update", { analytics_storage: "granted" });
      // Manual page_view management (SPA): no automatic hit from config.
      window.gtag("config", GA_ID, { send_page_view: false, anonymize_ip: true });
      resolve();
    };
    script.onerror = () => resolve(); // fail silently — never block the site
    document.head.appendChild(script);
  });
  return gaLoadPromise;
}

export const CookieConsent = () => {
  const { lang } = useLang();
  const location = useLocation();
  const [consent, setConsent] = useState(getConsent);
  const lastSent = useRef(null);
  const t = COPY[lang] || COPY.en;

  // Fire exactly one page_view per route once consent is given and GA is ready.
  useEffect(() => {
    if (consent !== "accepted") return;
    const key = `${location.pathname}${location.search}${location.hash}`;
    if (lastSent.current === key) return;
    lastSent.current = key;
    loadGa().then(() => {
      window.gtag("event", "page_view", {
        page_location: window.location.href,
        page_title: document.title,
      });
    });
  }, [consent, location]);

  // "Cookie settings" in the footer reopens the banner so visitors can
  // change or withdraw a previous decision.
  useEffect(() => {
    const reopen = () => setConsent(null);
    window.addEventListener("wd:open-cookie-settings", reopen);
    return () => window.removeEventListener("wd:open-cookie-settings", reopen);
  }, []);

  const choose = (value) => {
    const previous = getConsent();
    localStorage.setItem(CONSENT_KEY, value);
    if (value === "declined" && previous === "accepted") {
      // GA was already loaded this session — reload to clear its in-memory
      // state. After reload consent is "declined": banner stays hidden and
      // no GA script or page_view is ever sent.
      window.location.reload();
      return;
    }
    setConsent(value);
  };

  if (consent !== null) return null;

  return (
    <div
      role="dialog"
      aria-label={lang === "nl" ? "Cookie-toestemming" : "Cookie consent"}
      data-testid="cookie-consent-banner"
      className="fixed inset-x-4 bottom-4 z-[90] mx-auto max-w-xl rounded-2xl border border-ink/10 bg-cream/95 px-6 py-5 shadow-lg shadow-ink/10 backdrop-blur-md"
    >
      <p className="text-sm leading-relaxed text-soft">{t.text}</p>
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => choose("accepted")}
          data-testid="cookie-accept-button"
          className="rounded-full bg-clay px-6 py-2.5 text-sm tracking-wide text-cream transition-colors duration-300 hover:bg-rust"
        >
          {t.accept}
        </button>
        <button
          type="button"
          onClick={() => choose("declined")}
          data-testid="cookie-decline-button"
          className="rounded-full border border-clay px-6 py-2.5 text-sm tracking-wide text-clay transition-colors duration-300 hover:bg-clay hover:text-cream"
        >
          {t.decline}
        </button>
        <Link
          to="/privacy"
          data-testid="cookie-privacy-link"
          className="text-sm text-soft underline decoration-ink/20 underline-offset-4 transition-colors duration-300 hover:text-ink"
        >
          {t.privacy}
        </Link>
      </div>
    </div>
  );
};
